<template>
  <div v-if="rendered" class="gt-root">
    <!-- HEADER -->
    <div class="gt-header">
      <div class="gt-header-left">
        <div class="gt-header-icon">
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.2"
          >
            <path
              d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"
            />
            <polyline points="14 2 14 8 20 8" />
            <line x1="16" y1="13" x2="8" y2="13" />
            <line x1="16" y1="17" x2="8" y2="17" />
            <polyline points="10 9 9 9 8 9" />
          </svg>
        </div>
        <div>
          <h1 class="gt-title">Templates Push</h1>
          <p class="gt-sub">
            {{ templates.length }} template{{
              templates.length !== 1 ? "s" : ""
            }}
          </p>
        </div>
      </div>

      <div class="gt-header-right">
        <div class="gt-search-wrap">
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.5"
          >
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.35-4.35" />
          </svg>
          <input
            v-model="searchQuery"
            class="gt-search"
            placeholder="Pesquisar…"
          />
          <button
            v-if="searchQuery"
            class="gt-search-x"
            @click="searchQuery = ''"
          >
            ✕
          </button>
        </div>
        <button class="gt-create-btn" @click="abrirCriar">
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.5"
          >
            <path d="M12 5v14M5 12h14" />
          </svg>
          Novo template
        </button>
      </div>
    </div>

    <!-- EMPTY -->
    <div v-if="queriedData.length === 0 && !searchQuery" class="gt-empty">
      <div class="gt-empty-icon">
        <svg
          width="32"
          height="32"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
        >
          <path
            d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"
          />
          <polyline points="14 2 14 8 20 8" />
        </svg>
      </div>
      <h3>Nenhum template criado</h3>
      <p>Cria templates para reutilizar nas tuas campanhas Push.</p>
      <button class="gt-create-btn" @click="abrirCriar">
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2.5"
        >
          <path d="M12 5v14M5 12h14" />
        </svg>
        Criar primeiro template
      </button>
    </div>

    <div v-else-if="queriedData.length === 0 && searchQuery" class="gt-empty">
      <div class="gt-empty-icon">
        <svg
          width="32"
          height="32"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
        >
          <circle cx="11" cy="11" r="8" />
          <path d="m21 21-4.35-4.35" />
        </svg>
      </div>
      <h3>Sem resultados</h3>
      <p>Nenhum template corresponde a "{{ searchQuery }}".</p>
    </div>

    <!-- GRID DE CARDS -->
    <div v-else class="gt-grid">
      <div v-for="t in queriedData" :key="t.id" class="gt-card">
        <div class="gt-card-body">
          <div class="gt-card-top">
            <h3 class="gt-card-title">{{ t.titulo }}</h3>
            <span class="gt-card-date">{{ fmtDate(t.dt_created) }}</span>
          </div>
          <p class="gt-card-preview">{{ t.mensagem }}</p>
        </div>

        <div class="gt-card-footer">
          <button class="gt-action gt-action-primary" @click="usarTemplate(t)">
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.5"
            >
              <line x1="22" y1="2" x2="11" y2="13" />
              <polygon points="22 2 15 22 11 13 2 9 22 2" />
            </svg>
            Usar
          </button>
          <button class="gt-action" @click="abrirEditar(t)">
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.5"
            >
              <path
                d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"
              />
              <path
                d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"
              />
            </svg>
            Editar
          </button>
          <button
            class="gt-action gt-action-danger"
            @click="removerTemplate(t)"
          >
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.5"
            >
              <polyline points="3 6 5 6 21 6" />
              <path d="M19 6l-1 14H6L5 6" />
              <path d="M10 11v6M14 11v6" />
              <path d="M9 6V4h6v2" />
            </svg>
            Remover
          </button>
        </div>
      </div>
    </div>

    <!-- PAGINAÇÃO -->
    <div v-if="total > pagination.perPage" class="gt-pag">
      <span class="gt-pag-info">{{ from + 1 }}–{{ to }} de {{ total }}</span>
      <div class="gt-pag-btns">
        <button
          class="gt-pag-btn"
          :disabled="pagination.currentPage <= 1"
          @click="pagination.currentPage--"
        >
          ‹
        </button>
        <button
          v-for="p in pageNums"
          :key="p"
          class="gt-pag-btn"
          :class="{ cur: p === pagination.currentPage }"
          @click="pagination.currentPage = p"
        >
          {{ p }}
        </button>
        <button
          class="gt-pag-btn"
          :disabled="pagination.currentPage >= totalPages"
          @click="pagination.currentPage++"
        >
          ›
        </button>
      </div>
    </div>

    <!-- MODAL CRIAR / EDITAR -->
    <transition name="gt-fade">
      <div v-if="modalAberto" class="gt-overlay" @click.self="fecharModal">
        <div class="gt-modal">
          <div class="gt-modal-head">
            <div class="gt-modal-title">
              {{ modoEdicao ? "Editar template" : "Novo template" }}
            </div>
            <button class="gt-modal-close" @click="fecharModal">
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2.5"
              >
                <path d="M18 6 6 18M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div class="gt-modal-body">
            <label class="gt-label">Título</label>
            <input
              v-model="form.titulo"
              type="text"
              class="gt-input"
              placeholder="Ex: Promoção de verão"
            />

            <label class="gt-label"
              >Mensagem
              <span
                class="gt-char-count"
                :class="{ warn: form.mensagem.length < 30 }"
                >{{ form.mensagem.length }} caracteres{{
                  form.mensagem.length < 30 ? " (mínimo 30)" : ""
                }}</span
              ></label
            >
            <textarea
              v-model="form.mensagem"
              class="gt-textarea"
              rows="10"
              placeholder="Escreve a mensagem do template…"
            ></textarea>
          </div>

          <div class="gt-modal-foot">
            <button class="gt-btn gt-btn-ghost" @click="fecharModal">
              Cancelar
            </button>
            <button
              class="gt-btn gt-btn-primary"
              :disabled="saving"
              @click="guardar"
            >
              <span v-if="saving" class="gt-spinner"></span>
              <template v-else>
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="3"
                >
                  <path d="M20 6 9 17l-5-5" />
                </svg>
                Guardar
              </template>
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
  <LoadingOverlay v-else />
</template>

<script>
  import LoadingOverlay from "@/components/Loading/LoadingOverlay";
  import swal from "sweetalert2";
  import { campanhasService } from "@/services";

  export default {
    components: { LoadingOverlay },
    data() {
      return {
        rendered: false,
        templates: [],
        searchQuery: "",
        saving: false,
        modalAberto: false,
        modoEdicao: false,
        templateEditando: null,
        form: { titulo: "", mensagem: "" },
        pagination: { perPage: 12, currentPage: 1 },
      };
    },
    computed: {
      filteredData() {
        if (!this.searchQuery) return this.templates;
        const q = this.searchQuery.toLowerCase();
        return this.templates.filter(
          (t) =>
            t.titulo?.toLowerCase().includes(q) ||
            t.mensagem?.toLowerCase().includes(q),
        );
      },
      total() {
        return this.filteredData.length;
      },
      totalPages() {
        return Math.ceil(this.total / this.pagination.perPage) || 1;
      },
      from() {
        return (this.pagination.currentPage - 1) * this.pagination.perPage;
      },
      to() {
        return Math.min(this.from + this.pagination.perPage, this.total);
      },
      queriedData() {
        return this.filteredData.slice(this.from, this.to);
      },
      pageNums() {
        const p = this.pagination.currentPage,
          t = this.totalPages,
          r = [];
        for (let i = Math.max(1, p - 2); i <= Math.min(t, p + 2); i++)
          r.push(i);
        return r;
      },
    },
    watch: {
      searchQuery() {
        this.pagination.currentPage = 1;
      },
    },
    methods: {
      fmtDate(d) {
        if (!d) return "—";
        return new Date(d).toLocaleDateString("pt-PT", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        });
      },
      abrirCriar() {
        this.modoEdicao = false;
        this.templateEditando = null;
        this.form = { titulo: "", mensagem: "" };
        this.modalAberto = true;
      },
      abrirEditar(t) {
        this.modoEdicao = true;
        this.templateEditando = t;
        this.form = { titulo: t.titulo, mensagem: t.mensagem };
        this.modalAberto = true;
      },
      fecharModal() {
        this.modalAberto = false;
        this.templateEditando = null;
        this.form = { titulo: "", mensagem: "" };
      },
      async guardar() {
        if (!this.form.titulo.trim()) {
          this.$notify({
            type: "warning",
            title: "Título obrigatório",
            message: "Preenche o título do template.",
          });
          return;
        }
        if (this.form.mensagem.length < 30) {
          this.$notify({
            type: "warning",
            title: "Mensagem demasiado curta",
            message: "A mensagem deve ter pelo menos 30 caracteres.",
          });
          return;
        }
        this.saving = true;
        try {
          if (this.modoEdicao) {
            await campanhasService.updateTemplate(this.templateEditando.id, {
              titulo: this.form.titulo,
              mensagem: this.form.mensagem,
            });
            this.$notify({
              type: "success",
              title: "Atualizado",
              message: "Template atualizado com sucesso.",
            });
          } else {
            const data = await campanhasService.guardarTemplatePush(
              this.form.titulo,
              this.form.mensagem,
            );
            if (!data.success) {
              this.$notify({
                type: "danger",
                title: "Erro",
                message: data.error || "Não foi possível guardar.",
              });
              return;
            }
            this.$notify({
              type: "success",
              title: "Criado",
              message: "Template criado com sucesso.",
            });
          }
          await this.inicio();
          this.fecharModal();
        } catch (err) {
          this.$notify({
            type: "danger",
            title: "Erro",
            message:
              err?.response?.data?.error || "Ocorreu um erro. Tente novamente.",
          });
        } finally {
          this.saving = false;
        }
      },
      usarTemplate(t) {
        this.$router.push({
          path: "/campanhas/criar",
          query: { template_id: t.id },
        });
      },
      async removerTemplate(t) {
        const result = await swal.fire({
          title: "Remover template?",
          text: `Tens a certeza que queres remover "${t.titulo}"?`,
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "Remover",
          cancelButtonText: "Cancelar",
          confirmButtonColor: "#ff825d",
        });
        if (!result.value) return;
        try {
          await campanhasService.deleteTemplate(t.id);
          this.templates = this.templates.filter((x) => x.id !== t.id);
          this.$notify({
            type: "success",
            title: "Removido",
            message: "Template eliminado.",
          });
        } catch {
          this.$notify({
            type: "danger",
            title: "Erro",
            message: "Não foi possível remover.",
          });
        }
      },
      async inicio() {
        try {
          const resp = await campanhasService.getTemplatesWhatsapp();
          this.templates = resp.success && resp.templates ? resp.templates : [];
        } catch (err) {
          console.error("Erro:", err);
          this.$notify({
            type: "danger",
            title: "Erro",
            message: "Não foi possível carregar os templates.",
          });
        }
        this.rendered = true;
      },
    },
    async mounted() {
      await this.inicio();
    },
  };
</script>

<style scoped>
  .gt-root {
    padding: 28px 28px 48px;
    font-family: "DM Sans", "Outfit", system-ui, sans-serif;
    min-height: 100vh;
    background: #f8f6f3;
  }

  /* HEADER */
  .gt-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 28px;
    gap: 16px;
    flex-wrap: wrap;
  }
  .gt-header-left {
    display: flex;
    align-items: center;
    gap: 14px;
  }
  .gt-header-right {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
  }
  .gt-header-icon {
    width: 44px;
    height: 44px;
    flex-shrink: 0;
    background: #ff825d;
    border-radius: 13px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    box-shadow: 0 4px 14px rgba(255, 130, 93, 0.3);
  }
  .gt-title {
    font-size: 20px;
    font-weight: 800;
    color: #1a1210;
    margin: 0;
    letter-spacing: -0.4px;
  }
  .gt-sub {
    font-size: 13px;
    color: #b0a8a0;
    margin: 3px 0 0;
  }

  .gt-search-wrap {
    display: flex;
    align-items: center;
    gap: 8px;
    background: #fff;
    border: 1.5px solid #f0ece8;
    border-radius: 11px;
    padding: 9px 13px;
    transition: border-color 0.15s;
  }
  .gt-search-wrap:focus-within {
    border-color: #ff825d;
  }
  .gt-search {
    background: none;
    border: none;
    outline: none;
    font-size: 13.5px;
    color: #1a1210;
    width: 180px;
    font-family: inherit;
  }
  .gt-search::placeholder {
    color: #c0b8b0;
  }
  .gt-search-x {
    background: none;
    border: none;
    cursor: pointer;
    color: #c0b8b0;
    font-size: 12px;
    padding: 0;
  }

  .gt-create-btn {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    padding: 10px 18px;
    background: #ff825d;
    color: #fff;
    border: none;
    border-radius: 11px;
    font-size: 13.5px;
    font-weight: 600;
    cursor: pointer;
    box-shadow: 0 2px 8px rgba(255, 130, 93, 0.3);
    transition: all 0.15s;
    font-family: inherit;
    white-space: nowrap;
  }
  .gt-create-btn:hover {
    background: #e86b45;
    transform: translateY(-1px);
  }

  /* EMPTY */
  .gt-empty {
    text-align: center;
    padding: 80px 20px;
    color: #b0a8a0;
  }
  .gt-empty-icon {
    width: 72px;
    height: 72px;
    border-radius: 20px;
    background: #f5f2ef;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 20px;
    color: #c8c0b8;
  }
  .gt-empty h3 {
    font-size: 18px;
    font-weight: 700;
    color: #5a534f;
    margin-bottom: 8px;
  }
  .gt-empty p {
    font-size: 14px;
    color: #b0a8a0;
    margin-bottom: 24px;
  }

  /* GRID */
  .gt-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 14px;
    margin-bottom: 24px;
  }

  /* CARD */
  .gt-card {
    background: #fff;
    border: 1.5px solid #f0ece8;
    border-radius: 14px;
    display: flex;
    flex-direction: column;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04), 0 4px 12px rgba(0, 0, 0, 0.04);
    transition: box-shadow 0.15s, transform 0.15s;
    overflow: hidden;
  }
  .gt-card:hover {
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.06), 0 12px 28px rgba(0, 0, 0, 0.07);
    transform: translateY(-2px);
  }

  .gt-card-body {
    padding: 16px 18px 14px;
    flex: 1;
  }
  .gt-card-top {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 10px;
    margin-bottom: 10px;
  }
  .gt-card-title {
    font-size: 14.5px;
    font-weight: 700;
    color: #1a1210;
    margin: 0;
    letter-spacing: -0.2px;
    line-height: 1.3;
  }
  .gt-card-date {
    font-size: 11px;
    color: #c0b8b0;
    white-space: nowrap;
    flex-shrink: 0;
    margin-top: 2px;
  }
  .gt-card-preview {
    font-size: 13px;
    color: #7a736e;
    margin: 0;
    line-height: 1.55;
    display: -webkit-box;
    -webkit-line-clamp: 4;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  /* Card footer */
  .gt-card-footer {
    display: flex;
    gap: 2px;
    padding: 8px 10px;
    border-top: 1px solid #f8f5f2;
  }
  .gt-action {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 5px;
    padding: 7px 4px;
    border: none;
    background: none;
    cursor: pointer;
    font-size: 12px;
    font-weight: 600;
    color: #8a857e;
    border-radius: 8px;
    transition: all 0.12s;
    font-family: inherit;
  }
  .gt-action:hover {
    background: #f7f4f1;
    color: #1a1210;
  }
  .gt-action-primary:hover {
    background: #fff0eb;
    color: #ff825d;
  }
  .gt-action-danger:hover {
    background: #fef0ef;
    color: #c04820;
  }

  /* PAGINAÇÃO */
  .gt-pag {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 4px 0;
    flex-wrap: wrap;
    gap: 10px;
  }
  .gt-pag-info {
    font-size: 13px;
    color: #b0a8a0;
  }
  .gt-pag-btns {
    display: flex;
    gap: 4px;
  }
  .gt-pag-btn {
    min-width: 34px;
    height: 34px;
    padding: 0 6px;
    border: 1.5px solid #e8e4e0;
    border-radius: 9px;
    background: #fff;
    font-size: 13px;
    color: #6b6460;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.15s;
    font-family: inherit;
  }
  .gt-pag-btn:hover:not(:disabled) {
    border-color: #ff825d;
    color: #ff825d;
  }
  .gt-pag-btn:disabled {
    opacity: 0.35;
    cursor: default;
  }
  .gt-pag-btn.cur {
    background: #ff825d;
    border-color: #ff825d;
    color: #fff;
    font-weight: 700;
  }

  /* MODAL */
  .gt-overlay {
    position: fixed;
    inset: 0;
    background: rgba(15, 10, 8, 0.45);
    backdrop-filter: blur(6px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1200;
    padding: 16px;
  }
  .gt-modal {
    background: #fff;
    border-radius: 18px;
    width: 100%;
    max-width: 560px;
    max-height: 90vh;
    display: flex;
    flex-direction: column;
    box-shadow: 0 24px 80px rgba(0, 0, 0, 0.2);
    overflow: hidden;
  }
  .gt-modal-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 20px 24px 16px;
    border-bottom: 1px solid #f5f2ef;
  }
  .gt-modal-title {
    font-size: 16px;
    font-weight: 700;
    color: #1a1210;
  }
  .gt-modal-close {
    background: #f7f4f1;
    border: none;
    cursor: pointer;
    width: 30px;
    height: 30px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #7a736e;
    transition: all 0.12s;
  }
  .gt-modal-close:hover {
    background: #f0ece8;
    color: #1a1210;
  }

  .gt-modal-body {
    padding: 20px 24px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 14px;
  }
  .gt-label {
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 12px;
    font-weight: 700;
    color: #8a857e;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    margin-bottom: 6px;
  }
  .gt-char-count {
    font-size: 11px;
    font-weight: 500;
    color: #b0a8a0;
    text-transform: none;
    letter-spacing: 0;
  }
  .gt-char-count.warn {
    color: #e05a3a;
  }
  .gt-input {
    width: 100%;
    padding: 11px 14px;
    border: 1.5px solid #e8e4e0;
    border-radius: 10px;
    font-size: 14.5px;
    color: #1a1210;
    outline: none;
    transition: border-color 0.15s;
    font-family: inherit;
  }
  .gt-input:focus {
    border-color: #ff825d;
    box-shadow: 0 0 0 3px rgba(255, 130, 93, 0.12);
  }
  .gt-textarea {
    width: 100%;
    padding: 11px 14px;
    border: 1.5px solid #e8e4e0;
    border-radius: 10px;
    font-size: 14px;
    color: #1a1210;
    outline: none;
    resize: vertical;
    line-height: 1.55;
    font-family: inherit;
    transition: border-color 0.15s;
  }
  .gt-textarea:focus {
    border-color: #ff825d;
    box-shadow: 0 0 0 3px rgba(255, 130, 93, 0.12);
  }

  .gt-modal-foot {
    display: flex;
    gap: 8px;
    justify-content: flex-end;
    padding: 16px 24px;
    border-top: 1px solid #f5f2ef;
  }
  .gt-btn {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    padding: 10px 20px;
    border: none;
    border-radius: 10px;
    font-size: 13.5px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.15s;
    font-family: inherit;
  }
  .gt-btn-ghost {
    background: #f7f4f1;
    color: #5a534f;
  }
  .gt-btn-ghost:hover {
    background: #f0ece8;
  }
  .gt-btn-primary {
    background: #ff825d;
    color: #fff;
    box-shadow: 0 2px 8px rgba(255, 130, 93, 0.3);
  }
  .gt-btn-primary:hover:not(:disabled) {
    background: #e86b45;
  }
  .gt-btn-primary:disabled {
    opacity: 0.5;
    cursor: default;
  }
  .gt-spinner {
    width: 14px;
    height: 14px;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-top-color: #fff;
    border-radius: 50%;
    animation: spin 0.6s linear infinite;
  }
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }

  .gt-fade-enter-active {
    transition: opacity 0.2s;
  }
  .gt-fade-leave-active {
    transition: opacity 0.15s;
  }
  .gt-fade-enter,
  .gt-fade-leave-to {
    opacity: 0;
  }

  @media (max-width: 600px) {
    .gt-root {
      padding: 16px 12px 40px;
    }
    .gt-grid {
      grid-template-columns: 1fr;
    }
    .gt-search {
      width: 120px;
    }
  }
</style>
